import React, { useState } from 'react';
import personaInsightsService, { PersonaInsight, PersonaInsightsReport } from '../services/personaInsightsService';
import { Link } from 'react-router-dom';
import gtmService from '../services/gtmService';
import { gtmPfad } from '../vertrieb/gtmLinks';
import MarkdownView from '../components/MarkdownView';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const confidenceColor = (c: string): string =>
  c === 'high' ? 'var(--sec-success)' : c === 'medium' ? 'var(--sec-warning)' : 'var(--sec-danger)';

const PersonaInsightsPage: React.FC = () => {
  const [transcripts, setTranscripts] = useState('');
  const [focusPersona, setFocusPersona] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Gerade gespeichert: direkt im GTM-Setup oeffnen.
  const [gespeichertId, setGespeichertId] = useState<string | null>(null);
  const [report, setReport] = useState<PersonaInsightsReport | null>(null);
  const [raw, setRaw] = useState('');

  const handleAnalyze = async () => {
    if (!transcripts.trim()) {
      setError('Bitte Transcript(s) einfügen');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setInfo('');
      setReport(null);
      setRaw('');
      const result = await personaInsightsService.analyze(transcripts, focusPersona || undefined);
      if (result.report) setReport(result.report);
      else setRaw(result.raw || 'Kein strukturierter Report erhalten.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const saveAsPersona = async (p: PersonaInsight, idx: number) => {
    try {
      setSavingIdx(idx);
      setError('');
      const neu = await gtmService.createPersona({
        name: p.name,
        goals: p.goals.join('\n'),
        painPoints: p.pains.join('\n'),
        objections: p.objections.join('\n'),
      });
      setGespeichertId(neu?.id ?? null);
      setInfo(`Persona „${p.name}" als GTM-Persona gespeichert (Entwurf).`);
      setTimeout(() => setInfo(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern der Persona');
    } finally {
      setSavingIdx(null);
    }
  };

  const list = (label: string, items: string[]) =>
    items.length > 0 ? (
      <div style={{ marginTop: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--sec-fg-subtle)' }}>{label}</div>
        <ul style={{ margin: '2px 0 0 18px', fontSize: '13px', color: 'var(--sec-fg)' }}>
          {items.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <div className="page-container">
      <PageHeader title="Persona-Erkenntnisse (Persona Insights)" subtitle={routeById('persona-insights')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Analysiert Sales-Call-Transcripts und erzeugt einen strukturierten Persona-Report
        (Ziele, Pains, Einwände, Feature-Wünsche, O-Töne, Buying-Signale) — powered by Skill
        <em> persona-insights-analysis</em>. Jede Persona lässt sich als GTM-Persona speichern.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {info && (
        <div className="alert alert-success">
          {info}
          {gespeichertId && <> <Link to={gtmPfad('persona', gespeichertId)}>Im GTM-Setup öffnen</Link></>}
        </div>
      )}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div className="form-group">
          <label className="form-label">Transcript(s)</label>
          <textarea
            className="form-textarea"
            rows={10}
            value={transcripts}
            onChange={(e) => setTranscripts(e.target.value)}
            placeholder="Ein oder mehrere Call-Transcripts hier einfügen (Speaker-Turns, mehrere Calls hintereinander sind ok)…"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Fokus-Persona (optional)</label>
          <input
            className="form-input"
            value={focusPersona}
            onChange={(e) => setFocusPersona(e.target.value)}
            placeholder="z.B. VP Sales — leer lassen für automatische Gruppierung"
            style={{ maxWidth: '360px' }}
          />
        </div>
        <button className="btn btn-success" onClick={handleAnalyze} disabled={loading}>
          {loading ? 'Analysiere…' : 'Transcripts analysieren'}
        </button>
      </div>

      {raw && <MarkdownView content={raw} style={{ marginTop: '16px' }} />}

      {report && (
        <div style={{ marginTop: '20px' }}>
          <p style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginBottom: '12px' }}>
            {report.transcriptsAnalyzed} Transcript(s) analysiert · Confidence:{' '}
            <strong style={{ color: confidenceColor(report.confidence) }}>{report.confidence}</strong>
          </p>

          {report.personas.map((p, idx) => (
            <div className="card" key={idx} style={{ marginBottom: '12px' }}>
              <div className="card-header">
                <div className="card-title">{p.name}</div>
                <button className="btn btn-primary btn-sm" onClick={() => saveAsPersona(p, idx)} disabled={savingIdx === idx}>
                  {savingIdx === idx ? 'Speichere…' : 'Als GTM-Persona speichern'}
                </button>
              </div>
              {list('Ziele', p.goals)}
              {list('Pains', p.pains)}
              {list('Einwände', p.objections)}
              {list('Feature-Wünsche', p.featureRequests)}
              {list('Buying-Signale', p.buyingSignals)}
              {p.verbatims.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--sec-fg-subtle)' }}>O-Töne</div>
                  {p.verbatims.map((v, i) => (
                    <div key={i} style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', fontStyle: 'italic', marginTop: '2px' }}>
                      „{v}"
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {report.recommendations.length > 0 && (
            <div className="alert alert-success">
              <strong>Empfehlungen:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {report.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonaInsightsPage;
